import {
  ComplianceExtraFieldDefaultSettingFragment,
  ComplianceExtraFieldsConfigFragment,
  DefaultComplianceSettingsUpdateModalQueryDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { DefaultComplianceSettingsModal } from 'modules/finance/activities/defaultComplianceSettings/components/DefaultComplianceSettingsModal'
import { useDefaultComplianceSettingsForm } from 'modules/finance/activities/defaultComplianceSettings/hooks/useDefaultComplianceSettingsForm'

interface ModalContentProps {
  onClose: () => void
  refetchQuery?: string
  defaultComplianceExtraFieldSetting: ComplianceExtraFieldDefaultSettingFragment | null
  systemComplianceExtraFields: ComplianceExtraFieldsConfigFragment[]
  loading: boolean
}

const ModalContent = ({
  onClose,
  refetchQuery,
  defaultComplianceExtraFieldSetting,
  systemComplianceExtraFields,
  loading,
}: ModalContentProps) => {
  const {
    form,
    loading: submitting,
    resetForm,
  } = useDefaultComplianceSettingsForm({
    defaultValues: {
      legalEntity: defaultComplianceExtraFieldSetting?.legalEntity ?? null,
      complianceExtraFieldConfigId: (() => {
        const matchingField = systemComplianceExtraFields.find(
          (field) =>
            field.countryCode === (defaultComplianceExtraFieldSetting?.country ?? '') &&
            field.organization === (defaultComplianceExtraFieldSetting?.organization ?? '')
        )
        return matchingField ? matchingField.id : null
      })(),
      creditNoteDefaultValues: defaultComplianceExtraFieldSetting?.creditNoteDefaultValues ?? [],
      invoiceDefaultValues: defaultComplianceExtraFieldSetting?.invoiceDefaultValues ?? [],
    },
    refetchQuery,
    onSuccess: () => {
      resetForm()
      onClose()
    },
    successMessage: i18n.t('finance.compliance_extra_field_default_settings.modal.edit.success'),
    systemComplianceExtraFields,
  })

  const userClose = () => {
    resetForm()
    onClose()
  }

  return (
    <DefaultComplianceSettingsModal
      isOpen
      onClose={userClose}
      onSubmit={() => form.handleSubmit()}
      title={i18n.t('finance.compliance_extra_field_default_settings.modal.edit.title')}
      description={i18n.t('finance.compliance_extra_field_default_settings.modal.edit.description')}
      submitButtonText={i18n.t('common.save')}
      form={form}
      systemComplianceExtraFields={systemComplianceExtraFields}
      loading={loading}
      submitting={submitting}
      isUpdate
    />
  )
}

export const DefaultComplianceSettingsUpdateModal = ({
  defaultComplianceExtraFieldSettingId,
  isOpen,
  onClose,
  refetchQuery,
}: {
  defaultComplianceExtraFieldSettingId: number
  isOpen: boolean
  onClose: () => void
  refetchQuery?: string
}) => {
  const { data, loading } = useQuery(DefaultComplianceSettingsUpdateModalQueryDocument, {
    variables: {
      id: defaultComplianceExtraFieldSettingId,
    },
    skip: !isOpen,
  })
  const defaultComplianceExtraFieldSetting = data?.finance.complianceExtraFieldDefaultSetting
  const systemComplianceExtraFields = data?.finance.systemComplianceExtraFields ?? []

  if (!isOpen || !data) {
    return null
  }

  return (
    <ModalContent
      onClose={onClose}
      refetchQuery={refetchQuery}
      defaultComplianceExtraFieldSetting={defaultComplianceExtraFieldSetting || null}
      systemComplianceExtraFields={systemComplianceExtraFields}
      loading={loading}
    />
  )
}
